import { useHistory } from '@shared/core';
import { useEffect, useState, useRef, useMemo } from 'react';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { usePageRefFromUrl } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageRefFromUrl';
import { usePageInHistory } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageInHistory';
import { useAccommodationsEditorLeadStateQuery } from '../../../../../../admin/routes/WebsiteEditor/customComponents/Accommodations/Accommodations.graphql.generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isInIframe } from '@shared/utils/isInIframe';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type ScrollToPageWhenUrlChangesProps = Readonly<{
  pagesRef: ReadonlyArray<PagesRefs>;
  container: HTMLDivElement | null;
}>;

type UseScrollToPageResult = Readonly<{
  hasScrolled: boolean;
  currentPage: PagesRefs | null;
}>;

// Maximum retry attempts for layout completion (at 60fps = ~1 second)
const LAYOUT_RETRY_MAX_ATTEMPTS = 60;

/**
 * Handles scrolling to the correct page section when URL changes.
 *
 * For SSR pages: Scrolls immediately when elements are ready
 * For nossr=true (client-side only): Retries until layout is complete
 *
 * Retry mechanism:
 * - Checks if element has non-zero height (fully laid out)
 * - Retries on requestAnimationFrame until ready or timeout
 * - Prevents infinite loops with retry limit
 * - Handles zero-height pages (empty/hidden elements) with fallback
 *
 * Cleanup:
 * - Cancels animation frames before early returns to prevent stale closures
 * - Cancels on unmount/navigation to prevent memory leaks
 */
export const useScrollToPageWhenUrlChanges = ({ pagesRef, container }: ScrollToPageWhenUrlChangesProps): UseScrollToPageResult => {
  const history = useHistory();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasNormalizedUrl, setHasNormalizedUrl] = useState(false);
  const [currentPage, setCurrentPage] = useState<PagesRefs | null>(null);
  const previousPathnameRef = useRef<string | undefined>(undefined);
  const animationFrameIdRef = useRef<number | null>(null);
  const pages = useMemo(() => pagesRef.map(pr => pr.page), [pagesRef]);
  const getPageRefFromUrl = usePageRefFromUrl(pages);
  const replacePageInBrowserHistory = usePageInHistory(pages);

  const { eventHandle } = useEventInfo();
  const { data: accommodationsLeadStateData } = useAccommodationsEditorLeadStateQuery({
    variables: { eventHandle: eventHandle! },
    batchMode: 'fast',
    skip: !eventHandle,
    // Only used client-side in useEffect for scroll behavior — no need during SSR.
    ssr: false,
  });
  const hasBookingLink = !!accommodationsLeadStateData?.eventByName?.hotelRoomBlocks?.bookingLink;
  const [isInstantScrollEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useInstantScrollBehavior' });

  useEffect(() => {
    if (!hasNormalizedUrl && pagesRef?.length) {
      const pageRef = getPageRefFromUrl(pagesRef);

      // if the current slug is not valid, let's replace the url by the firsts available event page url
      if (!pageRef) {
        if (history.location.pathname.includes('accommodations') && hasBookingLink) {
          // intercept in the case of aloha to allow guest site to render bookinglink redirect component
          return;
        }
        replacePageInBrowserHistory(pagesRef[0].page);
      }

      setHasNormalizedUrl(true);
    }
  }, [pagesRef, hasNormalizedUrl, getPageRefFromUrl, replacePageInBrowserHistory, history.location.pathname, hasBookingLink]);

  useEffect(() => {
    // Cancel any previous retry chains FIRST, before any early returns
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    if (!pagesRef?.length || !container) {
      return;
    }

    const scrollToPage = (pageRef: PagesRefs, isSmooth: boolean) => {
      const scrollTop = container.scrollTop;
      const isFirstPage = pageRef.page.id === pagesRef[0].page.id;
      const pageRelativePositionToScreen = pageRef?.ref?.getBoundingClientRect?.()?.top || 0;
      const pagePosition = pageRelativePositionToScreen + scrollTop;
      const scrollPositionY = isFirstPage ? 0 : pagePosition;
      const behavior = isSmooth && !isInstantScrollEnabled ? 'smooth' : 'auto';

      container.scrollTo({ left: 0, top: scrollPositionY, behavior });
    };

    const goToPage = (pageRef: PagesRefs, hasScrolled: boolean) => {
      if (pageRef?.ref) {
        const attemptScroll = (retryCount = 0) => {
          const rect = pageRef.ref?.getBoundingClientRect();

          if (rect && rect.height > 0) {
            // Element has been laid out with non-zero height, scroll now
            scrollToPage(pageRef, hasScrolled);
            setCurrentPage(pageRef);
            setHasScrolled(true);
            animationFrameIdRef.current = null;
          } else if (retryCount < LAYOUT_RETRY_MAX_ATTEMPTS && pageRef.ref) {
            // Layout not complete yet, retry on next frame
            animationFrameIdRef.current = requestAnimationFrame(() => attemptScroll(retryCount + 1));
          } else {
            // Fallback: either hit retry limit or ref became null
            // Attempt scroll anyway (handles zero-height pages, hidden elements, etc.)
            scrollToPage(pageRef, hasScrolled);
            setCurrentPage(pageRef);
            setHasScrolled(true);
            animationFrameIdRef.current = null;
          }
        };

        // Call directly for first attempt - SSR pages scroll immediately,
        // nossr pages will schedule RAF retries as needed
        attemptScroll(0);
      }
    };

    // Go to the Page when Page loads the first time or when pathname changes via navigation
    const currentPathname = history.location.pathname;
    const isPathnameChanged = previousPathnameRef.current !== currentPathname;

    const isPushNavigationWithPathChange = history.action === 'PUSH' && isPathnameChanged;
    const shouldScroll = !hasScrolled || isPushNavigationWithPathChange;

    const isIframeReplaceTracking = hasScrolled && isInIframe() && history.action === 'REPLACE';

    if (shouldScroll && !isIframeReplaceTracking) {
      const pageRef = getPageRefFromUrl(pagesRef);

      if (pageRef) {
        goToPage(pageRef, hasScrolled);
      }
    }

    previousPathnameRef.current = currentPathname;

    // Cleanup animation frame on unmount or dependency change
    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [container, getPageRefFromUrl, hasScrolled, history.action, history.location.pathname, isInstantScrollEnabled, pagesRef, setCurrentPage, setHasScrolled]);

  return { hasScrolled, currentPage };
};
